import React from 'react';
import styled from 'styled-components';
import {ButtonDropdownField, DropdownList, tokensForThemeProvider} from '@forecast-it/design-system';
import {FormattedMessage} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {DateScope} from '../periodFilter';

const tokens = tokensForThemeProvider.sky.sys;

/** Render order and copy come from the design. `all_dates` is first because it is the default and
 * what every report saved before this control existed means. */
const DATE_SCOPES: DateScope[] = ['all_dates', 'start_date', 'deadline'];

/* Matches DateRangePresetDropdown's own override: this field carries the same 8px corner radius as
 * the adjacent date-range field (sky/sys/size/borderradius/3) rather than ButtonDropdownField's
 * tighter default. */
const RoundedButtonDropdownField = styled(ButtonDropdownField)`
	border-radius: ${tokens.size.borderradius['3']};
	/* The trigger sizes to its label rather than wrapping it. Every label here is a short phrase of
	 two or three words ("All Dates", "Deadlines only"), and the field's auto width otherwise lets
	 the flex row squeeze it until the label breaks onto a second line, which makes the control
	 taller than the ones beside it and misaligns the whole date group. */
	white-space: nowrap;
	flex-shrink: 0;
`;

/** The scope selector sitting beside the date-range controls: which date field(s) the range filters
 * on. Operates purely through useReportBuilder()'s period/setPeriodScope - it holds no local state
 * of its own, exactly like DateRangePresetDropdown. */
export function DateScopeDropdown(): JSX.Element {
	const {period, setPeriodScope} = useReportBuilder();
	const selected: DateScope = period.dateScope ?? 'all_dates';

	return (
		<DropdownList>
			<DropdownList.Trigger>
				<RoundedButtonDropdownField width={'auto'} data-cy={'ai-report-date-scope'}>
					<FormattedMessage id={`ai_report_builder.date_scope.${selected}`} />
				</RoundedButtonDropdownField>
			</DropdownList.Trigger>
			<DropdownList.Content offset={4} align={'start'}>
				{DATE_SCOPES.map(scope => (
					<DropdownList.Item
						key={scope}
						onClick={() => setPeriodScope(scope)}
						data-cy={`ai-report-date-scope-option-${scope}`}
					>
						<FormattedMessage id={`ai_report_builder.date_scope.${scope}`} />
					</DropdownList.Item>
				))}
			</DropdownList.Content>
		</DropdownList>
	);
}
